import React, { useMemo, useState } from 'react';
import {
  FiAlertCircle,
  FiEdit2,
  FiLayers,
  FiLoader,
  FiTrash2,
} from 'react-icons/fi';

import { Button, StatusPill, ToolbarSearch } from '@/shared/components/ui';
import type {
  AgentCategory,
  AgentSegment,
  AgentTestScenarioResponse,
} from '@troia-v3/types';

import { getToolLabel } from '../../constants/toolLabels';
import {
  useAgentScenarios,
  useDeleteScenario,
} from '../../hooks/useAgentTraining';

import { ScenarioFormModal } from './ScenarioFormModal';

/**
 * Labels locais pra render — mesmo padrão do AgentFormPage (robusto a
 * HMR do types package via .tgz).
 */
const CATEGORY_LABELS: Record<string, string> = {
  sdr: 'SDR',
  attendant: 'Atendente',
  support: 'Suporte',
  financial: 'Financeiro',
  sales: 'Vendas',
  scheduling: 'Agendamento',
  other: 'Outro',
};

const SEGMENT_LABELS: Record<string, string> = {
  educational: 'Educacional',
  real_estate: 'Imobiliária',
  healthcare: 'Saúde',
  ecommerce: 'E-commerce',
  services: 'Serviços',
  saas: 'SaaS',
  accounting: 'Contabilidade',
  law: 'Advocacia',
  food: 'Alimentação',
  automotive: 'Automotivo',
  solar_energy: 'Energia Solar',
  apparel: 'Confecção',
  generic: 'Genérico',
};

type AppliesToFilter = 'all' | 'this-agent';

interface ScenariosTabProps {
  /** Categoria do agente sendo editado — usado pra filtro "aplicável a este agente" */
  agentCategory?: AgentCategory;
  /** Segmento do agente sendo editado */
  agentSegment?: AgentSegment;
}

/**
 * Tab "Cenários" do AgentFormPage — lista apenas os cenários de teste
 * **customizados** do tenant. O cliente pode criar, editar e deletar
 * seus cenários; o treinamento os executa junto com os cenários do
 * sistema (estes nunca aparecem na UI, são a "inteligência interna"
 * da Troia).
 *
 * ## Filtros
 *
 * - **Aplicabilidade**: todos os custom OU apenas os que se aplicam ao
 *   agente atual (usa `agentCategory` + `agentSegment` via matching
 *   M-para-M no backend — cenários com arrays vazios são universais).
 * - **Busca**: por label, scenarioKey ou texto dos customerTurns (server-side)
 *
 * ## CTA
 *
 * Botão "Novo cenário" abre o `ScenarioFormModal` em modo criação, com
 * a categoria/segmento do agente atual pré-selecionados pra facilitar.
 * Editar/deletar abrem o modal no item correspondente.
 */
export const ScenariosTab: React.FC<ScenariosTabProps> = ({
  agentCategory,
  agentSegment,
}) => {
  const [appliesToFilter, setAppliesToFilter] =
    useState<AppliesToFilter>('this-agent');
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingScenario, setEditingScenario] =
    useState<AgentTestScenarioResponse | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  // ─── Query params ────────────────────────────────────────────────────
  // SEMPRE `isSystem: false` — cenários do sistema são internos da Troia
  // e não devem aparecer na UI do cliente.
  const queryParams = useMemo(() => {
    const params: Parameters<typeof useAgentScenarios>[0] = {
      isSystem: false,
      activeOnly: false, // Mostra inclusive os inativos pro cliente gerenciar
      limit: 100,
    };
    if (appliesToFilter === 'this-agent' && agentCategory) {
      params.agentCategory = agentCategory;
    }
    if (appliesToFilter === 'this-agent' && agentSegment) {
      params.agentSegment = agentSegment;
    }
    if (search.trim()) params.search = search.trim();
    return params;
  }, [appliesToFilter, agentCategory, agentSegment, search]);

  const { data, isLoading, error } = useAgentScenarios(queryParams);
  const deleteMutation = useDeleteScenario();

  // ─── Handlers ────────────────────────────────────────────────────────
  const handleCreate = (): void => {
    setEditingScenario(null);
    setShowModal(true);
  };

  const handleEdit = (scenario: AgentTestScenarioResponse): void => {
    setEditingScenario(scenario);
    setShowModal(true);
  };

  const handleConfirmDelete = async (id: string): Promise<void> => {
    try {
      await deleteMutation.mutateAsync(id);
      setConfirmDeleteId(null);
    } catch {
      // React Query já expõe o erro — o banner abaixo não é crítico aqui
      setConfirmDeleteId(null);
    }
  };

  const handleModalSaved = (): void => {
    setShowModal(false);
    setEditingScenario(null);
  };

  // ─── Derivados ───────────────────────────────────────────────────────
  const scenarios = data?.items ?? [];

  // ─── Render ──────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      {/* Info banner */}
      <div
        className="rounded-lg border p-4 flex items-start gap-3"
        style={{
          backgroundColor: 'rgba(139, 92, 246, 0.04)',
          borderColor: 'rgba(139, 92, 246, 0.20)',
        }}
      >
        <FiLayers
          className="h-5 w-5 flex-shrink-0 mt-0.5"
          style={{ color: '#8b5cf6' }}
        />
        <div className="flex-1">
          <div className="text-sm font-semibold text-slate-800 mb-1">
            Seus cenários de teste
          </div>
          <p className="text-xs text-slate-600 leading-relaxed">
            Cenários são mensagens simuladas de clientes usadas no treinamento
            do agente para avaliar qualidade das respostas. Crie cenários
            próprios pra testar situações específicas do seu negócio — eles
            serão executados junto com os cenários padrão do TroiaChat quando
            você treinar o agente.
          </p>
          {!agentCategory && !agentSegment && (
            <div className="text-xs mt-2 flex items-center gap-1.5 text-amber-700">
              <FiAlertCircle className="h-3.5 w-3.5" />
              Defina categoria e segmento do agente na aba "Informações
              Básicas" para filtrar automaticamente os cenários aplicáveis.
            </div>
          )}
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
        <div className="flex-1 min-w-0">
          {/* F17-22: busca canônica de toolbar (ToolbarSearch) */}
          <ToolbarSearch
            value={search}
            onChange={setSearch}
            placeholder="Buscar por título, chave ou mensagem…"
            data-testid="scenarios-search"
          />
        </div>

        <div className="flex items-center gap-2">
          {(agentCategory || agentSegment) && (
            <SegmentedControl
              options={[
                { value: 'this-agent', label: 'Deste agente' },
                { value: 'all', label: 'Todos' },
              ]}
              value={appliesToFilter}
              onChange={(v) => setAppliesToFilter(v as AppliesToFilter)}
            />
          )}

          <Button
            variant="primary"
            icon="plus"
            onClick={handleCreate}
            data-testid="scenario-create"
          >
            Novo cenário
          </Button>
        </div>
      </div>

      {/* Contador */}
      {!isLoading && data && (
        <div className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
          {data.total} {data.total === 1 ? 'cenário' : 'cenários'}{' '}
          {data.total === 1 ? 'encontrado' : 'encontrados'}
        </div>
      )}

      {/* Lista */}
      {isLoading && (
        <div className="flex items-center gap-2 text-sm text-slate-500 py-8 justify-center">
          <FiLoader className="h-4 w-4 animate-spin" />
          Carregando cenários...
        </div>
      )}

      {error && (
        <div
          className="rounded-md px-4 py-3 text-sm"
          style={{
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            color: '#991b1b',
          }}
        >
          Erro ao carregar cenários: {error.message}
        </div>
      )}

      {!isLoading && !error && scenarios.length === 0 && (
        <div
          className="rounded-lg border p-8 text-center"
          style={{
            backgroundColor: 'var(--theme-card-background)',
            borderColor: 'var(--theme-card-border)',
          }}
        >
          <FiLayers
            className="h-10 w-10 mx-auto mb-3"
            style={{ color: 'var(--theme-muted-text)' }}
          />
          <div
            className="text-sm font-semibold mb-1"
            style={{ color: 'var(--theme-card-text)' }}
          >
            Nenhum cenário encontrado
          </div>
          <p
            className="text-xs mb-4"
            style={{ color: 'var(--theme-muted-text)' }}
          >
            {search
              ? 'Tente ajustar os filtros ou busca.'
              : 'Crie seu primeiro cenário customizado para testar situações específicas do seu negócio.'}
          </p>
          <Button
            variant="primary"
            icon="plus"
            onClick={handleCreate}
            data-testid="scenario-create-empty"
          >
            Criar cenário
          </Button>
        </div>
      )}

      {!isLoading && scenarios.length > 0 && (
        <div className="space-y-2">
          {scenarios.map((scenario) => (
            <ScenarioRow
              key={scenario.id}
              scenario={scenario}
              onEdit={() => handleEdit(scenario)}
              onRequestDelete={() => setConfirmDeleteId(scenario.id)}
              isDeleteConfirming={confirmDeleteId === scenario.id}
              isDeleting={deleteMutation.isPending}
              onConfirmDelete={() => handleConfirmDelete(scenario.id)}
              onCancelDelete={() => setConfirmDeleteId(null)}
            />
          ))}
        </div>
      )}

      {/* Modal de criação/edição */}
      <ScenarioFormModal
        isOpen={showModal}
        scenario={editingScenario}
        {...(agentCategory ? { defaultCategory: agentCategory } : {})}
        {...(agentSegment ? { defaultSegment: agentSegment } : {})}
        onClose={() => {
          setShowModal(false);
          setEditingScenario(null);
        }}
        onSaved={handleModalSaved}
      />
    </div>
  );
};

// ─── SegmentedControl ───────────────────────────────────────────────────

interface SegmentedControlProps {
  options: Array<{ value: string; label: string }>;
  value: string;
  onChange: (value: string) => void;
}

const SegmentedControl: React.FC<SegmentedControlProps> = ({
  options,
  value,
  onChange,
}) => {
  return (
    <div
      className="flex rounded-lg border overflow-hidden"
      style={{ borderColor: 'var(--theme-input-border)' }}
    >
      {options.map((opt, idx) => {
        const selected = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className="px-3 py-2 text-xs font-medium transition-colors"
            style={{
              backgroundColor: selected
                ? 'rgba(139, 92, 246, 0.1)'
                : 'var(--theme-input-background)',
              color: selected ? '#8b5cf6' : 'var(--theme-muted-text)',
              borderLeft:
                idx > 0 ? `1px solid var(--theme-input-border)` : 'none',
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
};

// ─── ScenarioRow ────────────────────────────────────────────────────────

interface ScenarioRowProps {
  scenario: AgentTestScenarioResponse;
  onEdit: () => void;
  onRequestDelete: () => void;
  isDeleteConfirming: boolean;
  isDeleting: boolean;
  onConfirmDelete: () => void;
  onCancelDelete: () => void;
}

const ScenarioRow: React.FC<ScenarioRowProps> = ({
  scenario,
  onEdit,
  onRequestDelete,
  isDeleteConfirming,
  isDeleting,
  onConfirmDelete,
  onCancelDelete,
}) => {
  const [expanded, setExpanded] = useState(false);

  // Na UI atual só exibimos cenários customizados (sistema fica escondido),
  // então sempre podemos editar/deletar. A flag fica como guard defensivo
  // caso o filtro mude no futuro.
  const canEdit = !scenario.isSystem;
  const applicableToCategories =
    scenario.categories.length === 0
      ? 'qualquer categoria'
      : scenario.categories
          .map((c) => CATEGORY_LABELS[c] ?? c)
          .join(', ');
  const applicableToSegments =
    scenario.segments.length === 0
      ? 'qualquer segmento'
      : scenario.segments
          .map((s) => SEGMENT_LABELS[s] ?? s)
          .join(', ');

  return (
    <div
      className="rounded-lg border p-4 transition-colors"
      style={{
        backgroundColor: 'var(--theme-card-background)',
        borderColor: scenario.active
          ? 'var(--theme-card-border)'
          : 'rgba(245, 158, 11, 0.30)',
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h4
              className="text-sm font-semibold"
              style={{ color: 'var(--theme-card-text)' }}
            >
              {scenario.label}
            </h4>
            {!scenario.active && (
              /* F62: pill de status padrão (inativo = neutro) */
              <StatusPill variant="neutral" label="Inativo" data-testid={`scenario-inactive-${scenario.id}`} />
            )}
          </div>

          <div
            className="text-xs mb-2"
            style={{ color: 'var(--theme-muted-text)' }}
          >
            <code className="font-mono">{scenario.scenarioKey}</code> ·{' '}
            {applicableToCategories} · {applicableToSegments}
          </div>

          <div
            className="text-xs rounded px-3 py-2"
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.03)',
              color: 'var(--theme-card-text)',
            }}
          >
            <div
              className="text-[10px] uppercase tracking-wide mb-1 font-semibold"
              style={{ color: 'var(--theme-muted-text)' }}
            >
              {scenario.customerTurns.length === 1
                ? '1 turno'
                : `${scenario.customerTurns.length} turnos`}
            </div>
            <div className="italic line-clamp-2">
              "{scenario.customerTurns[0] ?? ''}"
              {scenario.customerTurns.length > 1 && (
                <span className="not-italic font-medium" style={{ color: 'var(--theme-muted-text)' }}>
                  {' '}… +{scenario.customerTurns.length - 1}
                </span>
              )}
            </div>
          </div>

          {expanded && (
            <div className="mt-3 space-y-2">
              <div>
                <div
                  className="text-xs font-semibold mb-1"
                  style={{ color: 'var(--theme-muted-text)' }}
                >
                  Rubric
                </div>
                <div
                  className="text-xs rounded px-3 py-2"
                  style={{
                    backgroundColor: 'rgba(0, 0, 0, 0.03)',
                    color: 'var(--theme-card-text)',
                  }}
                >
                  {scenario.rubric}
                </div>
              </div>

              <div
                className="text-xs"
                style={{ color: 'var(--theme-muted-text)' }}
              >
                Score mín. de relevância:{' '}
                <strong>
                  {(scenario.expectedMinRelevance * 100).toFixed(0)}%
                </strong>
              </div>

              {scenario.expectedTools && scenario.expectedTools.length > 0 && (
                <div>
                  <div
                    className="text-xs mb-1"
                    style={{ color: 'var(--theme-muted-text)' }}
                  >
                    Ferramentas esperadas:
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {scenario.expectedTools.map((t) => (
                      <span
                        key={t}
                        className="text-xs px-2 py-0.5 rounded-md font-medium"
                        style={{
                          backgroundColor: 'rgba(16, 185, 129, 0.1)',
                          color: '#047857',
                        }}
                      >
                        {getToolLabel(t)}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {scenario.forbiddenTools && scenario.forbiddenTools.length > 0 && (
                <div>
                  <div
                    className="text-xs mb-1"
                    style={{ color: 'var(--theme-muted-text)' }}
                  >
                    Ferramentas proibidas:
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {scenario.forbiddenTools.map((t) => (
                      <span
                        key={t}
                        className="text-xs px-2 py-0.5 rounded-md font-medium"
                        style={{
                          backgroundColor: 'rgba(239, 68, 68, 0.1)',
                          color: '#b91c1c',
                        }}
                      >
                        {getToolLabel(t)}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className="text-xs mt-2 font-medium"
            style={{ color: '#8b5cf6' }}
          >
            {expanded ? 'Ver menos' : 'Ver detalhes'}
          </button>
        </div>

        {canEdit && !isDeleteConfirming && (
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              type="button"
              onClick={onEdit}
              className="p-2 rounded-md transition-colors"
              style={{ color: '#64748b' }}
              aria-label="Editar cenário"
              data-testid={`scenario-edit-${scenario.id}`}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f1f5f9';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <FiEdit2 className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={onRequestDelete}
              className="p-2 rounded-md transition-colors"
              style={{ color: '#64748b' }}
              aria-label="Deletar cenário"
              data-testid={`scenario-delete-${scenario.id}`}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#fef2f2';
                e.currentTarget.style.color = '#991b1b';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#64748b';
              }}
            >
              <FiTrash2 className="h-4 w-4" />
            </button>
          </div>
        )}

        {isDeleteConfirming && (
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={onCancelDelete}
              disabled={isDeleting}
              className="px-3 py-1.5 rounded-md text-xs font-medium"
              style={{ color: '#64748b', backgroundColor: 'transparent' }}
              data-testid={`scenario-delete-cancel-${scenario.id}`}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={onConfirmDelete}
              disabled={isDeleting}
              className="px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1"
              style={{ backgroundColor: '#dc2626', color: '#ffffff' }}
              data-testid={`scenario-delete-confirm-${scenario.id}`}
            >
              {isDeleting && <FiLoader className="h-3 w-3 animate-spin" />}
              Confirmar
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
